import React, { useRef } from "react";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  PictureOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  DownOutlined,
} from "@ant-design/icons";

// Same Style/Heading options as the article editor's own block-type select
// (NormalEditor.js's ARTICLE_BLOCK_OPTIONS) — kept as a separate constant
// here rather than importing that one, since it's internal to
// NormalEditor.js and not exported.
const BLOCK_STYLE_OPTIONS = [
  { label: "Style", value: "unstyled" },
  { label: "Heading 1", value: "header-one" },
  { label: "Heading 2", value: "header-two" },
  { label: "Heading 3", value: "header-three" },
];

// Single shared toolbar for the whole BlogDescriptionModal document. Every
// outline-section NormalEditor now renders with hideToolbar={true} (see
// BlogDescriptionModal.js) — this is the ONE toolbar for the whole modal,
// pinned near the top of the scrollable body, and it drives whichever
// editor instance last fired onFocus.
//
// `editorRefsMap` (key -> NormalEditor ref) and `activeKeyRef` (the key of
// the currently-focused editor) are both refs, not values — every button
// resolves `editorRefsMap.current[activeKeyRef.current]?.current` FRESH at
// click time rather than caching the target object anywhere. This matters:
// NormalEditor's imperative handle is rebuilt on every one of its own
// re-renders (i.e. every keystroke), so caching a snapshot of it earlier
// (e.g. at focus time) goes stale the moment the user types — see the long
// comment in BlogDescriptionModal.js for the bug that caused (typed text
// reverting, characters appearing reversed).
function BlogDescriptionToolbar({
  editorRefsMap,
  activeKeyRef,
  activeStyles = [],
  activeBlockType = null,
  onUploadImage,
}) {
  const fileInputRef = useRef(null);

  const getActiveEditor = () => editorRefsMap?.current?.[activeKeyRef?.current]?.current || null;

  const withActiveEditor = (fn) => (e) => {
    e.preventDefault();
    const editor = getActiveEditor();
    if (!editor) return;
    fn(editor);
  };

  const isActive = (style) => activeStyles.includes(style);
  const isActiveBlock = (blockType) => activeBlockType === blockType;

  const handleImageClick = (e) => {
    e.preventDefault();
    if (!getActiveEditor()) return;
    fileInputRef.current?.click();
  };

  // Selecting a heading (or "Style", to clear one) SETS the current block
  // to that type — RichUtils.toggleBlockType (which NormalEditor's exposed
  // toggleBlockType wraps) only "toggles off" back to unstyled when the
  // block is ALREADY that exact type, so picking Heading 2 while on
  // Heading 1 correctly switches rather than stacking. Same semantics as
  // the article editor's own Style select in NormalEditor.js.
  const handleStyleChange = (e) => {
    const editor = getActiveEditor();
    if (!editor) return;
    editor.toggleBlockType(e.target.value);
  };

  const currentStyleValue = ["header-one", "header-two", "header-three"].includes(activeBlockType)
    ? activeBlockType
    : "unstyled";

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const editor = getActiveEditor();
    if (!editor) return;
    try {
      const res = await onUploadImage(file);
      const url = res?.data?.link;
      if (url) editor.insertImage(url);
    } catch (err) {
      // Upload failure is surfaced by onUploadImage's own axios call site
      // conventions elsewhere in this app; nothing further to do here.
    }
  };

  return (
    <div className="blog-desc-toolbar">
      <label className="blog-desc-toolbar-style-select">
        <select
          value={currentStyleValue}
          onChange={handleStyleChange}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {BLOCK_STYLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <DownOutlined className="blog-desc-toolbar-style-caret" />
      </label>
      <span className="blog-desc-toolbar-divider" />
      <button
        type="button"
        className={`blog-desc-toolbar-btn${isActive("BOLD") ? " active" : ""}`}
        title="Bold"
        aria-label="Bold"
        onMouseDown={(e) => e.preventDefault()}
        onClick={withActiveEditor((editor) => editor.toggleInlineStyle("BOLD"))}
      >
        <BoldOutlined />
      </button>
      <button
        type="button"
        className={`blog-desc-toolbar-btn${isActive("ITALIC") ? " active" : ""}`}
        title="Italic"
        aria-label="Italic"
        onMouseDown={(e) => e.preventDefault()}
        onClick={withActiveEditor((editor) => editor.toggleInlineStyle("ITALIC"))}
      >
        <ItalicOutlined />
      </button>
      <button
        type="button"
        className={`blog-desc-toolbar-btn${isActive("UNDERLINE") ? " active" : ""}`}
        title="Underline"
        aria-label="Underline"
        onMouseDown={(e) => e.preventDefault()}
        onClick={withActiveEditor((editor) => editor.toggleInlineStyle("UNDERLINE"))}
      >
        <UnderlineOutlined />
      </button>
      <span className="blog-desc-toolbar-divider" />
      <button
        type="button"
        className={`blog-desc-toolbar-btn${isActiveBlock("unordered-list-item") ? " active" : ""}`}
        title="Bulleted list"
        aria-label="Bulleted list"
        onMouseDown={(e) => e.preventDefault()}
        onClick={withActiveEditor((editor) => editor.toggleBlockType("unordered-list-item"))}
      >
        <UnorderedListOutlined />
      </button>
      <button
        type="button"
        className={`blog-desc-toolbar-btn${isActiveBlock("ordered-list-item") ? " active" : ""}`}
        title="Numbered list"
        aria-label="Numbered list"
        onMouseDown={(e) => e.preventDefault()}
        onClick={withActiveEditor((editor) => editor.toggleBlockType("ordered-list-item"))}
      >
        <OrderedListOutlined />
      </button>
      <span className="blog-desc-toolbar-divider" />
      <button
        type="button"
        className="blog-desc-toolbar-btn"
        title="Insert image"
        aria-label="Insert image"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleImageClick}
      >
        <PictureOutlined />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
}

export default BlogDescriptionToolbar;
